import { useDraggable } from '@dnd-kit/core';
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import { FieldDisplay } from '../../../fields/display/FieldDisplay';
import { TagDot } from '../../../fields/display/TagDot';
import { type FieldContextValue, FieldProvider } from '../../../fields/FieldContext';
import { cx, Icon, type TagTone, useIsDesktop } from '../../../ui';
import { MOTION, useMotion } from '../../../ui/motion';
import { RecordRowMenu } from '../table/RecordRowMenu';
import { useRowSelection } from '../table/RowSelectionProvider';
import { KanbanCardFrame } from './frames';
import { cardDragId } from './moveTarget';

/**
 * A CARD IS THE TABLE'S ROW, ON A BOARD (wave 5 round 4, B4; spec/screens/list.md section 2
 * item 12 and spec/components/MAP.md "Board columns": "cards = the table row's content
 * (identifier, one secondary line, status dot) at 54 px plus the secondary line").
 *
 * WHAT IT WAS. The title, a grip, and TWO field values drawn as a definition list - label in a
 * 64px column, value beside it - which is a table wearing a card's clothes: the reader already
 * knows what the view shows, and the phone list next door dropped exactly those labels for
 * exactly that reason (`RecordCard`, "ONE SECONDARY LINE, VALUES ONLY"). Two labelled rows also
 * put a card at 96px against a row token of 54.
 *
 * WHAT IT IS. One 54px identifier line - the status dot, the name, the drag affordance - and
 * ONE secondary line of values under it. The values render through `FieldDisplay` with
 * `tagAsDot`, which is the product's read-surface rule (`FieldContext.tagAsDot`, owner
 * 2026-09-10): a select is text with a dot in front of it and never a capsule, so a board card
 * cannot grow a `Badge` the way the column header had.
 *
 * THE STATUS DOT IS THE COLUMN'S OWN OPTION COLOUR, and that is a derivation rather than a
 * spec line: the table draws a select value as dot-plus-text, the board spends the column
 * HEADER on the label, so what is left for the card is the dot. It is the one thing that still
 * says where a card belongs while it is lifted out of its column. An option with no colour
 * gets no dot - SelectDisplay's own rule, because an invented grey circle is a second encoding
 * of nothing.
 *
 * The whole card is the drag activator, not a grip that only appears on hover - on a touch
 * screen there is no hover. The grip glyph is therefore always visible and purely an
 * affordance.
 */

/** The record's title. TEXT is the normal case; anything else is still shown. */
export function recordTitle(record: RecordRow, labelField: FieldMetadata | undefined): string {
  const value = labelField === undefined ? undefined : record.values[labelField.id];
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return 'Untitled';
}

export interface KanbanCardBodyProps {
  readonly objectId: string;
  readonly record: RecordRow;
  readonly labelField: FieldMetadata | undefined;
  /**
   * ONE FIELD (B4). The spec's card is one secondary LINE, and a line holds one value that can
   * truncate; two would either wrap or fight for the same 320px.
   */
  readonly secondaryFields: readonly FieldMetadata[];
  /**
   * The column's option colour, or `null` when the option carries none and for the no-value
   * bucket. Handed down rather than read off the record: during an optimistic move the
   * record's value has already changed while the column's query has not, and the dot has to
   * agree with the column the reader is looking at.
   */
  readonly tone: TagTone | null;
}

/** The visible content, shared by the card in the column and the drag overlay. */
export function KanbanCardBody({
  objectId,
  record,
  labelField,
  secondaryFields,
  tone,
}: KanbanCardBodyProps) {
  const secondary = secondaryFields[0];
  return (
    <>
      {/* The identifier line at the table's own row height, so the two views of one list
          measure the same. The title truncates to one line, so the dot and the grip centre
          against it instead of being nudged by a hand-tuned margin. */}
      <div className="board-card-title">
        {tone !== null && <TagDot tone={tone} />}
        <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
          {recordTitle(record, labelField)}
        </p>
        <Icon name="grip" size="1rem" className="shrink-0 text-ink-subtle" />
      </div>
      {secondary !== undefined && (
        <CardSecondaryLine objectId={objectId} record={record} field={secondary} />
      )}
    </>
  );
}

/**
 * ONE GREY LINE, VALUES ONLY - the phone list's construction (`RecordCard`), because the card
 * and the phone row are the same answer to the same question. `tagAsDot` is what keeps a
 * select in it as text with a dot rather than as the capsule `Badge` draws.
 */
function CardSecondaryLine({
  objectId,
  record,
  field,
}: {
  readonly objectId: string;
  readonly record: RecordRow;
  readonly field: FieldMetadata;
}) {
  const context = useMemo<FieldContextValue>(
    () => ({
      objectId,
      recordId: record.id,
      field,
      value: record.values[field.id] ?? null,
      // The board never edits inline: a card is a summary, and a mis-tap on a
      // card that is being dragged must not change data. The callback exists
      // because one context shape serves displays and inputs alike.
      onChange: () => undefined,
      readOnly: true,
      tagAsDot: true,
      humanOwned: record.provenance[field.id] !== undefined,
    }),
    [objectId, record, field],
  );

  return (
    <div className="board-card-secondary">
      <FieldProvider value={context}>
        <FieldDisplay />
      </FieldProvider>
    </div>
  );
}

export interface KanbanCardProps extends KanbanCardBodyProps {
  /** The object itself, for the row menu the board shares with the table. */
  readonly object: ObjectMetadata;
  /** The view's fields, which is where the row menu resolves its stage submenu from. */
  readonly fields: readonly FieldMetadata[];
  /** The field the board groups by. On a board the stage IS the column field. */
  readonly groupFieldId: string;
  readonly onOpen: (recordId: string) => void;
  /**
   * THE CARD'S PRESENCE IN ITS COLUMN (wave 5 round 4, M3). `false` means this record has
   * left the column and the card is being kept in its slot until its own fade has run;
   * `onExited` is how the column learns it may drop it. `usePresentRows` in `KanbanColumn`
   * owns both, the same hook the filter strip's tokens are held by.
   */
  readonly present: boolean;
  readonly onExited: () => void;
}

export function KanbanCard({
  object,
  fields,
  groupFieldId,
  objectId,
  record,
  labelField,
  secondaryFields,
  tone,
  onOpen,
  present,
  onExited,
}: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, isDragging, activatorEvent } = useDraggable({
    id: cardDragId(record.id),
    attributes: { roleDescription: 'Board card', tabIndex: 0 },
  });

  /**
   * A CARD ENTERS AND LEAVES ITS COLUMN ON THE ROW FAMILY (`docs/decisions/motion.md`
   * section 2, "List row insert and remove"; `MOTION.row` draws Apple's own `effectFade`).
   *
   * WHY A BOARD NEEDS IT MORE THAN A TABLE DOES. A move on this board is a card leaving one
   * list and arriving in another, and both halves were happening between two frames: the
   * card vanished from the column it was in and appeared in the column next to it with
   * nothing to connect the two events. The drag says where the card went while the pointer
   * is down; the menu path ("Set Stage") says nothing at all. The fade is what makes both
   * ends of the move legible, and the same fade runs whether a drop, the row menu or
   * somebody else's change moved the record.
   *
   * The SLOT is the layout's, not this element's: the recipe's Row block is explicit that a
   * height animation would make every card below travel for a change that was not theirs.
   *
   * TWO REFS ON ONE ELEMENT. dnd-kit owns the node as a draggable and `useMotion` owns it as
   * a surface with an exit; both are callback refs, so the card hands the node to each. Both
   * are stable across renders - `useMotion`'s is a `useCallback` with no dependencies - so
   * this one is memoised too and dnd-kit is never handed a detach-and-reattach it did not ask
   * for.
   */
  const motion = useMotion(present, { onExited });
  const motionRef = motion.ref;
  const setCardRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      motionRef(node);
    },
    [setNodeRef, motionRef],
  );

  // A POINTER drag that ends where it started also fires a click on the card, and
  // opening the record then would be navigation nobody asked for - so that click
  // is swallowed. A keyboard drag fires no click, and arming the guard for it
  // would swallow the next real tap instead.
  const draggedSinceLastClick = useRef(false);
  useEffect(() => {
    if (isDragging && activatorEvent !== null && 'pointerId' in activatorEvent) {
      draggedSinceLastClick.current = true;
    }
  }, [isDragging, activatorEvent]);

  /**
   * A CLICK SELECTS AND A DOUBLE CLICK OPENS, WHICH IS THE TABLE'S RULE (wave 5 round 3,
   * C3-3). The board grew the list's detail pane this round, and a pane is about a
   * SELECTION: a single click that navigated away would mean the pane could never be shown
   * anything. `RecordTableRow` has made exactly this choice since P4-2 and this is the same
   * selection object, so the two views of one list behave the same way.
   *
   * BELOW THE DESKTOP BREAKPOINT A TAP STILL OPENS. There is no pane at those widths
   * (`.list-detail` is `display: none` under 64rem), the columns are snap stops the width of
   * the screen, and a tap that only tinted a card would be a tap that did nothing.
   */
  const selection = useRowSelection();
  const isDesktop = useIsDesktop();
  const selected = selection.isSelected(record.id);
  /**
   * The menu acts on the whole selection when this card is in it, and on this card alone
   * otherwise - the table's rule (`RecordTableRow`), resolved here for the same reason: the
   * answer is a property of the card that was clicked.
   */
  const targetIds = selected ? selection.selection.ids : [record.id];

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (draggedSinceLastClick.current) {
      draggedSinceLastClick.current = false;
      return;
    }
    if (!isDesktop) {
      onOpen(record.id);
      return;
    }
    selection.click(record.id, event);
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    // Space starts the drag (see the KeyboardSensor codes in KanbanView), so
    // Enter is what opens a record - and role="button" on a div does not
    // synthesise a click, which is why this listener exists at all. Keyup
    // rather than keydown keeps it clear of dnd-kit's own keydown activator.
    if (event.key === 'Enter' && !isDragging) {
      onOpen(record.id);
    }
  };

  return (
    /**
     * THE CARD CARRIES THE TABLE'S OWN ROW MENU (B4; spec/screens/list.md section 2 item 12:
     * "'Move To...' in the row menu is the keyboard path"). The board had no menu at all, so
     * the ONLY way to move a record between columns was a pointer drag - which is no path for
     * a keyboard, for a screen reader, or for a touch user who cannot hold and travel. The
     * menu is `RecordRowMenu`, unchanged, because a board and a table are two views of one
     * list and one operation may not grow two names: its stage submenu is resolved over the
     * view's fields by `resolveBoardGroupField`, which on a board IS the column field, so
     * "Set Stage -> Active" and a drop into Active write the same value through the same
     * mutation. Ledger P4-W5-21 records why it is not literally titled "Move To...".
     */
    <RecordRowMenu
      object={object}
      fields={fields}
      targetIds={targetIds}
      /* The board's columns are the group field's options and its queries ask for none of the
         tombstones, so every card on it is a live record: the menu's exit is Archive, never
         Restore. The archived segment is the table's (`RecordListPage`), which is where a
         tombstone is read and restored. */
      archived={false}
      boardGroupFieldId={groupFieldId}
      onOpen={() => selection.contextClick(record.id)}
    >
      {/* The role and tabIndex are spelled out below rather than left to dnd-kit's
        spread (which sets the same values): the card is the thing you press, and a
        reader should not have to look inside dnd-kit to find that out. A native
        <button> is not an option here - field displays render links and the phone
        display renders its own button, and neither may nest inside a button. */}
      {/* A `role="button"` box and not a `<button>`: a button cannot contain links or buttons. */}
      <KanbanCardFrame
        ref={setCardRef}
        {...attributes}
        {...(listeners ?? {})}
        role="button"
        tabIndex={0}
        /* THE SELECTED STATE IS `aria-pressed`, NOT `aria-selected` (R6 A8, O8). The card is
         a `role="button"`, and ARIA does not define `aria-selected` on a button - the roles
         that carry it are option, row, gridcell, tab and treeitem, none of which may contain
         the links and buttons a field display renders. `aria-pressed` is the state ARIA
         defines on a button, it holds for several cards at once (the selection is the
         table's multi-selection, not one current item, which is why this is not the
         `aria-current` the single-selection queues use), and a reader announces the card
         as pressed or not pressed. Desktop only, like `data-selected`: below the
         breakpoint a tap opens the record and there is no selection to announce, so the
         card keeps dnd-kit's own value (pressed while it is being dragged, the spread's
         default, which this prop would otherwise overwrite). `data-selected` stays as the hook the CSS paints the two AppKit selection roles
         from. */
        aria-pressed={isDesktop ? selected : attributes['aria-pressed']}
        data-selected={isDesktop && selected ? 'true' : undefined}
        data-dragging={isDragging ? 'true' : undefined}
        data-state={motion.state}
        /* A card on its way out is no longer a target: its record is already in another
           column, so a click on it would select something the reader cannot see. */
        inert={!present}
        onClick={handleClick}
        onDoubleClick={() => onOpen(record.id)}
        onKeyUp={handleKeyUp}
        /* THE FRAME IS THE BOARD'S SHARED CARD (`frames.tsx`, BR-PARTS): the white rounded
           box, its hairline and the darker hairline under the pointer. The rest is this
           board's: the drag cursor, the touch floor and the row motion. */
        className={cx(
          'board-card touch-target cursor-grab',
          // `manipulation` keeps the browser's pan gesture, which is what lets a
          // swipe scroll the board while only a long press starts a drag.
          'touch-manipulation select-none',
          MOTION.row,
        )}
      >
        <KanbanCardBody
          objectId={objectId}
          record={record}
          labelField={labelField}
          secondaryFields={secondaryFields}
          tone={tone}
        />
      </KanbanCardFrame>
    </RecordRowMenu>
  );
}
